import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Show from "../../../widgets/Show";

import moment from "moment";
import RichTextEditor from "./rich-text-editor/Quill";
import { Field, useFormik, FormikProvider } from "formik";
import { SelectPicker } from "../../../select-picker/SelectPicker";
import { FULL_DATE_FORMAT } from "../../../../constants/Constants";
import ChartClinicalNoteCreatedDate from "./ChartClinicalNoteCreatedDate";

const enhancer = connect(
  ({ chartSideClinicalNote, session: { permissions }, chart: { appointment } }) => {
    return {
      initialValues: {
        name: chartSideClinicalNote.item.name,
        note: chartSideClinicalNote.item.note,
        referenceClinicNoteId: chartSideClinicalNote.item.referenceClinicNoteId,
      },
      permissions,
      appointment,
      addendumId: chartSideClinicalNote.addendumId,
    };
  },
);

const ChartClinicalNoteTextareaForm = (props) => {
  const { onSubmit, initialValues, addendumNames, edit, item, addendumId } = props;
  const [showAddendumNameSelect, setShowAddendumNameSelect] = useState(false);

  const addendumOptions = useMemo(() => {
    if (!addendumNames) return [];

    return addendumNames.map((item, index) => {
      return {
        value: index,
        label: item,
      };
    });
  }, [addendumNames]);

  const isOther = (item) => {
    if (!item || !item?.label) return false;

    return item.label.toLowerCase() === "other";
  };

  const [selectedAddendumName, setSelectedAddendumName] = useState(null);

  const selectedItem = useMemo(
    () => addendumOptions?.find((item) => item.value === selectedAddendumName),
    [addendumOptions, selectedAddendumName],
  );

  useEffect(() => {
    if (edit && selectedAddendumName === null) {
      const initialNoteName = addendumOptions?.find(
        (i) => i.label.toLowerCase() === initialValues.name?.toLowerCase(),
      );

      if (initialNoteName) {
        setSelectedAddendumName(initialNoteName.value);
        setShowAddendumNameSelect(true);
      }
    }

    if (!edit && addendumId > 0) {
      const defaultNoteName = addendumOptions?.find((item) => isOther(item));
      setSelectedAddendumName(defaultNoteName?.value ?? null);
      setShowAddendumNameSelect(true);
    }
  }, [edit, addendumOptions, initialValues.name, selectedAddendumName, addendumId]);

  const form = useFormik({
    initialValues,
    onSubmit: (values) => {
      if (values.note) {
        values.note = values?.note
          .split("\n")
          .map((line) => `<p>${line}</p>`)
          .join("\n");
      }
      onSubmit(values);
    },
  });

  useEffect(
    () => () => {
      form.resetForm();
    },
    [],
  );

  return (
    <FormikProvider value={form}>
      <form onSubmit={form.handleSubmit} className="clinical-note-textarea">
        <div className="d-flex flex-column justify-content-end align-items-end mb-1">
          <div className="d-flex justify-content-start align-items-start">
            <span className="mr-2">{item.patient && item.patient.chartNumber}</span>
            <span className="mr-2">{item.patient && item.patient.fullName}</span>
            {item?.appointment?.date && (
              <span className="mr-2">
                Appt date: {moment(item?.appointment.date).format(FULL_DATE_FORMAT)}
              </span>
            )}
            <span className="mr-2">
              Being Seen date:{" "}
              {item?.appointment?.actualStartTime
                ? moment(item?.appointment.actualStartTime).format(FULL_DATE_FORMAT)
                : ""}
            </span>
            <span className="mr-2">
              Completed date:{" "}
              {item?.appointment?.modifiedDate
                ? moment(item?.appointment.modifiedDate).format(FULL_DATE_FORMAT)
                : ""}
            </span>
          </div>
        </div>
        <div className="clinical-note-textarea-name">
          {showAddendumNameSelect && (
            <SelectPicker
              className="clinical-note-addendum-name-select"
              value={{ value: selectedAddendumName }}
              onChange={(x) => {
                setSelectedAddendumName(x.value);
                const noteName = isOther(x) ? "" : x.label;
                form.setFieldValue("name", noteName);
              }}
              options={addendumOptions}
            />
          )}

          <input
            name="name"
            type="text"
            readOnly={showAddendumNameSelect && !isOther(selectedItem)}
            className="form-control clinical-note-textarea-name-input"
            placeholder="Note Name"
            {...form.getFieldProps("name")}
          />
        </div>
        <div className="clinical-note-textarea-body">
          <Field
            name="note"
            id="clinical-note-textarea-body"
            className="form-control"
            {...form.getFieldProps("note")}
          >
            {() => (
              <RichTextEditor value={form.values.note} change={form.setFieldValue} name="note" />
            )}
          </Field>
        </div>
        <div className="clinical-note-textarea-footer">
          <Show if={edit}>
            {/*could be updated with permission*/}
            <ChartClinicalNoteCreatedDate changeCreatedDate={props.changeCreatedDate} item={item} />
          </Show>
          <div className="btn-group w-100">
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: "50%" }}>
              Save
            </button>
            <button
              type="button"
              onClick={() => onSubmit({ ...form.values, sendToHie: true })}
              className="btn btn-danger btn-lg"
              style={{ width: "50%" }}
            >
              Save [Send to HIE]
            </button>
          </div>
        </div>
      </form>
    </FormikProvider>
  );
};

export default enhancer(ChartClinicalNoteTextareaForm);
